// Anke, 2026-07-21 — Gruppieren und Auflösen als rückgängig-machbare Befehle.
//
// Beide arbeiten ausschliesslich auf `groupId`-Verweisen und dem Anlegen bzw.
// Entfernen des Gruppen-Objekts. Positionen und Grössen der Mitglieder werden
// NICHT angefasst: Gruppieren soll nichts verschieben, es zieht nur eine
// Klammer. Darum ist der Rückweg auch verlustfrei.

import type { Command } from '@o2d/history';
import type { BoardState } from '../state/board-state.js';
import type { BoardObject, GroupObject } from '../interfaces/board-object.interface.js';
import { groupMembers, isGroup } from '../state/group.helper.js';

/** Merkt sich, woran ein Objekt VOR dem Befehl hing — für den Rückweg. */
interface Membership {
  readonly id: string;
  readonly previousGroupId: string | undefined;
}

/**
 * Fasst die übergebenen Objekte zu einer neuen Gruppe zusammen.
 *
 * Der Aufrufer übergibt bereits die ÄUSSERSTEN Vorfahren (siehe
 * `groupingCandidates`) und das fertige Gruppen-Objekt. Damit bleibt dieser
 * Befehl dumm — die Hierarchie-Regeln liegen in `group.helper.ts`, wo sie ohne
 * Board prüfbar sind.
 */
export class GroupObjectsCommand implements Command<BoardState> {
  readonly label = 'Gruppieren';
  readonly #previous: ReadonlyArray<Membership>;

  constructor(
    private readonly _group: GroupObject,
    private readonly _memberIds: ReadonlyArray<string>,
    previous: ReadonlyArray<Membership>,
  ) {
    this.#previous = previous;
  }

  apply(state: BoardState): void {
    state.addObject(this._group);
    for (const id of this._memberIds) {
      const obj = state.getObject(id);
      if (obj) state.replaceObject(id, { ...obj, groupId: this._group.id } as BoardObject);
    }
  }

  revert(state: BoardState): void {
    for (const { id, previousGroupId } of this.#previous) {
      const obj = state.getObject(id);
      if (!obj) continue;
      const next = { ...obj } as BoardObject & { groupId?: string };
      if (previousGroupId === undefined) delete next.groupId;
      else next.groupId = previousGroupId;
      state.replaceObject(id, next);
    }
    state.removeObject(this._group.id);
  }

  /** Die neue Gruppe — der Aufrufer wählt sie anschliessend aus. */
  get groupId(): string {
    return this._group.id;
  }
}

/**
 * Löst die übergebenen Gruppen auf — genau EINE Hierarchieebene (Ankes Punkt 3).
 *
 * Die direkten Mitglieder erben den Platz der aufgelösten Gruppe: Sie bekommen
 * deren eigenes `groupId`. Lag die Gruppe selbst in einer Übergruppe, bleiben
 * die Mitglieder also dort drin — es fällt nur die eine Ebene weg. War sie
 * ganz aussen, sind die Mitglieder danach frei.
 *
 * Verschachtelte Untergruppen bleiben unangetastet und sind danach die neuen
 * äussersten. Genau daraus ergibt sich das schichtweise Abtragen bei mehrfachem
 * Drücken.
 */
export class UngroupObjectsCommand implements Command<BoardState> {
  readonly label = 'Gruppierung aufheben';
  readonly #groups: ReadonlyArray<GroupObject>;
  readonly #previous: ReadonlyArray<Membership>;

  constructor(groups: ReadonlyArray<GroupObject>, previous: ReadonlyArray<Membership>) {
    this.#groups = groups;
    this.#previous = previous;
  }

  apply(state: BoardState): void {
    for (const group of this.#groups) {
      const parentId = group.groupId;
      for (const child of state.allObjects().filter((o) => o.groupId === group.id)) {
        const next = { ...child } as BoardObject & { groupId?: string };
        if (parentId === undefined) delete next.groupId;
        else next.groupId = parentId;
        state.replaceObject(child.id, next);
      }
      state.removeObject(group.id);
    }
  }

  revert(state: BoardState): void {
    for (const group of this.#groups) state.addObject(group);
    for (const { id, previousGroupId } of this.#previous) {
      const obj = state.getObject(id);
      if (!obj) continue;
      const next = { ...obj } as BoardObject & { groupId?: string };
      if (previousGroupId === undefined) delete next.groupId;
      else next.groupId = previousGroupId;
      state.replaceObject(id, next);
    }
  }

  /**
   * Was nach dem Auflösen ausgewählt sein soll: die freigewordenen Mitglieder.
   * Sonst zeigte die Auswahl auf eine Gruppe, die es nicht mehr gibt.
   */
  get freedIds(): ReadonlyArray<string> {
    return this.#previous.map((p) => p.id);
  }
}

/**
 * Die Zugehörigkeiten aller Objekte einsammeln, die ein Befehl anfassen wird.
 * Ohne diese Momentaufnahme wüsste der Rückweg nicht, wohin die Objekte gehören.
 */
export function captureMemberships(
  ids: ReadonlyArray<string>,
  state: BoardState,
): Membership[] {
  return ids
    .map((id) => state.getObject(id))
    .filter((o): o is BoardObject => !!o)
    .map((o) => ({ id: o.id, previousGroupId: o.groupId }));
}

/** Die direkten Mitglieder einer Gruppe (eine Ebene, nicht rekursiv). */
export function directMemberIds(groupId: string, state: BoardState): string[] {
  return state.allObjects().filter((o) => o.groupId === groupId).map((o) => o.id);
}

/** Alle Objekte einer Gruppe, rekursiv — für Löschen und Verschieben. */
export function allMemberIds(groupId: string, state: BoardState): string[] {
  return groupMembers(groupId, state.allObjects()).map((o) => o.id);
}

/** Nur die Gruppen aus einer Id-Liste. */
export function groupsOf(ids: ReadonlyArray<string>, state: BoardState): GroupObject[] {
  return ids
    .map((id) => state.getObject(id))
    .filter((o): o is BoardObject => !!o)
    .filter(isGroup);
}
